import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';

const stats = [
  { number: '100万+', label: '活跃用户', icon: 'ri-user-heart-line' },
  { number: '50万+', label: '优质文章', icon: 'ri-article-line' },
  { number: '99.9%', label: '服务可用率', icon: 'ri-cloud-line' },
  { number: '24/7', label: '客户服务', icon: 'ri-customer-service-2-line' },
];

const milestones = [
  { year: '2023', title: '平台创立', description: '17c.cc 正式上线，致力于为用户提供优质的综合服务平台。' },
  { year: '2024', title: '功能升级', description: '推出智能分析、团队协作等核心功能，用户数量突破 10 万。' },
  { year: '2025', title: '快速发展', description: '平台完成重大技术架构升级，服务能力大幅提升，用户突破 50 万。' },
  { year: '2026', title: '生态完善', description: '完善内容生态体系，推出移动端适配方案，活跃用户突破 100 万。' },
];

const testimonials = [
  {
    name: '张先生',
    role: '资深用户',
    content: '17c.cc 是我用过最好用的综合平台，界面简洁直观，功能强大。使用指南写得很详细，新手也能快速上手。',
    rating: 5,
  },
  {
    name: '李女士',
    role: '企业用户',
    content: '我们团队一直在用 17c.cc 进行数据管理和协作，效率提升非常明显。客户服务也很到位，问题都能及时解决。',
    rating: 5,
  },
  {
    name: '王先生',
    role: '新用户',
    content: '刚接触 17c.cc，按照使用指南一步步操作，很快就掌握了核心功能。平台的帮助文档非常全面。',
    rating: 5,
  },
];

const aboutStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于 17c.cc',
  description: '了解 17c.cc 的品牌故事、发展历程、团队理念及联系方式。17c.cc 是您不可错过的优质平台。',
  isPartOf: {
    '@type': 'WebSite',
    name: '17c.cc',
    url: 'https://17c.cc',
  },
};

const orgStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '17c.cc',
  url: 'https://17c.cc',
  description: '17c.cc 优质综合平台，提供官方入口、使用指南、功能介绍及问题解答',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'support@17c.cc',
    availableLanguage: ['Chinese'],
  },
};

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解 17c.cc 的品牌故事、发展历程、团队理念及联系方式。17c.cc 致力于为用户提供优质的综合服务平台体验。"
        keywords="17c.cc,17c.cc关于我们,17c.cc品牌介绍,17c.cc联系方式,17c.cc官方入口"
        canonicalUrl="https://17c.cc/about"
        structuredData={{ ...aboutStructuredData, ...orgStructuredData }}
      />

      <main className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <h1 className="text-2xl md:text-5xl font-heading font-bold text-foreground-900 mb-6">
                关于 17c.cc
              </h1>
              <p className="text-base md:text-lg text-foreground-500 max-w-3xl mx-auto leading-relaxed">
                17c.cc 是一家专注于提供优质综合服务的平台。自创立以来，我们始终秉承「用户至上」的理念，
                致力于为每一位用户打造流畅、安全、高效的使用体验。
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                    {stat.number}
                  </div>
                  <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-10 text-center">
                品牌故事
              </h2>
              <div className="prose-custom text-foreground-700 leading-relaxed space-y-6">
                <p>
                  17c.cc 的创立源于一个简单的信念：让每一个用户都能轻松享受优质的数字服务。
                  在数字化浪潮席卷全球的时代，我们发现许多用户在使用各类平台时，常常面临入口难找、操作复杂、信息混乱等问题。
                </p>
                <p>
                  为此，我们汇聚了一批充满热情的技术专家和产品设计师，共同打造了 17c.cc 这个平台。
                  我们希望通过简洁明了的界面设计、详尽易懂的使用指南和及时有效的客服支持，
                  让每一位用户都能快速上手，享受流畅便捷的在线体验。
                </p>
                <p>
                  经过多年的持续优化和改进，17c.cc 已经发展成为拥有超过百万活跃用户的综合性平台。
                  未来，我们将继续倾听用户的声音，不断迭代产品，为用户创造更多价值。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-14 text-center">
              发展历程
            </h2>
            <div className="max-w-3xl mx-auto">
              {milestones.map((m, index) => (
                <div key={m.year} className="flex gap-6 pb-10 last:pb-0">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold flex-shrink-0">
                      {m.year}
                    </div>
                    {index < milestones.length - 1 && (
                      <div className="w-0.5 flex-1 bg-primary-200 mt-2"></div>
                    )}
                  </div>
                  <div className="pt-1.5">
                    <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">
                      {m.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{m.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-14 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((t) => (
                <div key={t.name} className="p-8 rounded-xl bg-background-100">
                  <div className="flex items-center gap-1 mb-4 text-accent-500">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 text-sm font-bold">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6 text-center">
              联系我们
            </h2>
            <p className="text-center text-foreground-500 mb-12">
              如果您有任何问题或建议，欢迎通过以下方式联系我们
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="p-6 rounded-xl bg-background-50 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">电子邮箱</h3>
                <p className="text-sm text-foreground-500">support@17c.cc</p>
              </div>
              <div className="p-6 rounded-xl bg-background-50 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-50 text-accent-500">
                  <i className="ri-global-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">官方网站</h3>
                <p className="text-sm text-foreground-500">17c.cc</p>
              </div>
              <div className="p-6 rounded-xl bg-background-50 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-50 text-secondary-500">
                  <i className="ri-time-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">服务时间</h3>
                <p className="text-sm text-foreground-500">7×24 小时在线</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}